import {
  useCallback,
  type Dispatch,
  type SetStateAction,
  type RefObject
} from 'react';
import type { AnyAction } from '@reduxjs/toolkit';
import { fetchMomentDataCached } from '../../../services/momentsApi';
import type { MomentData } from '../../../services/momentsApi';
import { mergeMomentData } from '../utils/mergeMomentData';
import {
  getMomentFromPatchEnvelope,
  getNotificationsFromPatchEnvelope,
  type SlideoutSeamlessRefreshOptions
} from '../utils/patchEnvelopeSync';
import type { FeaturesContainerRef } from '../Features';

export type UseSlideoutSeamlessRefreshParams = {
  issueId: string | undefined;
  dispatch: Dispatch<AnyAction>;
  alertsActions: {
    loadMomentNotifications: (payload: { issueId: string }) => AnyAction;
    momentNotificationsLoaded: (payload: {
      issueId: string;
      response: any;
    }) => AnyAction;
    loadAlertsAndNotifications?: () => AnyAction;
  };
  setApiData: Dispatch<SetStateAction<MomentData | null>>;
  setDaciContacts: Dispatch<SetStateAction<any>>;
  setMilestoneUpdateTrigger: Dispatch<SetStateAction<number>>;
  featuresContainerRef: RefObject<FeaturesContainerRef | null>;
};

/**
 * Post-PATCH moment merge + optional features/notifications refresh.
 * Extracted from SlideOutPanel/index.tsx (Part 2A modularisation).
 */
export function useSlideoutSeamlessRefresh({
  issueId,
  dispatch,
  alertsActions,
  setApiData,
  setDaciContacts,
  setMilestoneUpdateTrigger,
  featuresContainerRef
}: UseSlideoutSeamlessRefreshParams) {
  const applyMomentDataToSlideout = useCallback(
    (
      momentData: Record<string, unknown>,
      options?: SlideoutSeamlessRefreshOptions
    ) => {
      setApiData(prevApiData => mergeMomentData(prevApiData, momentData));
      setDaciContacts(prevDaciContacts =>
        Object.prototype.hasOwnProperty.call(momentData, 'daciContacts')
          ? (momentData.daciContacts as typeof prevDaciContacts) ?? null
          : prevDaciContacts
      );
      setMilestoneUpdateTrigger(prev => prev + 1);
      if (options?.refreshFeatures !== false && featuresContainerRef.current) {
        featuresContainerRef.current.refresh();
      }
    },
    [
      setApiData,
      setDaciContacts,
      setMilestoneUpdateTrigger,
      featuresContainerRef
    ]
  );

  const refreshMomentNotifications = useCallback(
    (patchResponse?: unknown) => {
      if (!issueId) return;

      const notificationsFromPatch =
        getNotificationsFromPatchEnvelope(patchResponse);
      if (notificationsFromPatch?.syncStatus === 'FRESH') {
        dispatch(
          alertsActions.momentNotificationsLoaded({
            issueId,
            response: notificationsFromPatch
          })
        );
      }

      dispatch(alertsActions.loadMomentNotifications({ issueId }));
    },
    [issueId, dispatch, alertsActions]
  );

  const handleSeamlessDataRefresh = useCallback(
    async (
      patchResponse?: unknown,
      options?: SlideoutSeamlessRefreshOptions
    ) => {
      if (!issueId) return;

      const momentFromPatch = getMomentFromPatchEnvelope(patchResponse);
      if (momentFromPatch) {
        applyMomentDataToSlideout(momentFromPatch, options);
        return;
      }

      try {
        const response = await fetchMomentDataCached(issueId, true);

        if (response.success && response.data) {
          applyMomentDataToSlideout(
            response.data as unknown as Record<string, unknown>,
            options
          );
        }
      } catch (error) {
        // Don't show error to user - this is background refresh
      }
    },
    [issueId, applyMomentDataToSlideout]
  );

  const seamlessRefreshAndNotifications = useCallback(
    async (
      patchResponse?: unknown,
      options?: SlideoutSeamlessRefreshOptions
    ) => {
      await handleSeamlessDataRefresh(patchResponse, options);
      refreshMomentNotifications(patchResponse);
    },
    [handleSeamlessDataRefresh, refreshMomentNotifications]
  );

  const handleSeamlessDataRefreshWithoutFeatures = useCallback(
    (patchResponse?: unknown) =>
      handleSeamlessDataRefresh(patchResponse, { refreshFeatures: false }),
    [handleSeamlessDataRefresh]
  );

  const seamlessRefreshWithoutFeatures = useCallback(
    (patchResponse?: unknown) =>
      seamlessRefreshAndNotifications(patchResponse, {
        refreshFeatures: false
      }),
    [seamlessRefreshAndNotifications]
  );

  return {
    applyMomentDataToSlideout,
    refreshMomentNotifications,
    handleSeamlessDataRefresh,
    seamlessRefreshAndNotifications,
    handleSeamlessDataRefreshWithoutFeatures,
    seamlessRefreshWithoutFeatures
  };
}
